// frontend/src/hooks/useQuickPhrases.js
/**
 * Hook for ;-triggered quick phrase autocomplete in textareas
 *
 * Usage:
 * const {
 *   phrases,
 *   showDropdown,
 *   dropdownPosition,
 *   searchQuery,
 *   filteredPhrases,
 *   selectedIndex,
 *   handleKeyDown,
 *   handleInput,
 *   selectPhrase,
 *   closeDropdown
 * } = useQuickPhrases(textareaRef, onInsertPhrase);
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPhrases } from '../components/JournalEditor/api/journalApi';

export function useQuickPhrases(textareaRef, onTextChange) {
  // All phrases loaded from API
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);

  // Autocomplete state
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [triggerPosition, setTriggerPosition] = useState(null); // Position of ; character

  // Track if we're in autocomplete mode
  const isAutocompleteActive = useRef(false);

  // Load phrases on mount
  useEffect(() => {
    loadPhrases();
  }, []);

  const loadPhrases = async () => {
    try {
      setLoading(true);
      const data = await getPhrases();
      // Combine personal and clinic phrases, sort alphabetically
      const allPhrases = [
        ...(data.personal_phrases || []),
        ...(data.clinic_phrases || [])
      ].sort((a, b) => a.title.localeCompare(b.title, 'sv'));
      setPhrases(allPhrases);
    } catch (error) {
      console.error('Error loading quick phrases:', error);
      setPhrases([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter phrases based on search query
  const filteredPhrases = searchQuery
    ? phrases.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : phrases;

  // Calculate dropdown position based on cursor
  const calculateDropdownPosition = useCallback(() => {
    if (!textareaRef?.current) return { top: 0, left: 0 };

    const textarea = textareaRef.current;
    const rect = textarea.getBoundingClientRect();

    // Get cursor position in text
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = textarea.value.substring(0, cursorPos);

    // Create a temporary span to measure text position
    const mirror = document.createElement('div');
    const computed = window.getComputedStyle(textarea);

    // Copy styles
    mirror.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre-wrap;
      word-wrap: break-word;
      font-family: ${computed.fontFamily};
      font-size: ${computed.fontSize};
      line-height: ${computed.lineHeight};
      padding: ${computed.padding};
      width: ${textarea.clientWidth}px;
    `;

    mirror.textContent = textBeforeCursor;
    document.body.appendChild(mirror);

    // Get the last line
    const lines = textBeforeCursor.split('\n');
    const lastLine = lines[lines.length - 1];

    // Calculate approximate position
    const lineHeight = parseInt(computed.lineHeight) || parseInt(computed.fontSize) * 1.2;
    const charWidth = parseInt(computed.fontSize) * 0.6; // Approximate

    const top = rect.top + (lines.length * lineHeight) - textarea.scrollTop + 24;
    const left = rect.left + (lastLine.length * charWidth);

    document.body.removeChild(mirror);

    return {
      top: Math.min(top, window.innerHeight - 250),
      left: Math.min(left, window.innerWidth - 320)
    };
  }, [textareaRef]);

  // Handle input changes - detect ; trigger
  const handleInput = useCallback((e) => {
    const textarea = e.target;
    const value = textarea.value;
    const cursorPos = textarea.selectionStart;

    // Check if user just typed ;
    if (value[cursorPos - 1] === ';' && !isAutocompleteActive.current) {
      // Start autocomplete
      isAutocompleteActive.current = true;
      setTriggerPosition(cursorPos - 1);
      setSearchQuery('');
      setSelectedIndex(0);
      setDropdownPosition(calculateDropdownPosition());
      setShowDropdown(true);
      return;
    }

    // If autocomplete is active, update search query
    if (isAutocompleteActive.current && triggerPosition !== null) {
      // Get text after ; up to cursor
      const textAfterTrigger = value.substring(triggerPosition + 1, cursorPos);

      // Check if user typed space or moved away from trigger
      if (textAfterTrigger.includes(' ') || textAfterTrigger.includes('\n')) {
        closeDropdown();
        return;
      }

      setSearchQuery(textAfterTrigger);
      setSelectedIndex(0);
    }
  }, [triggerPosition, calculateDropdownPosition]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!showDropdown) return false;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredPhrases.length - 1 ? prev + 1 : prev
        );
        return true;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        return true;

      case 'Enter':
      case 'Tab':
        // Alltid svälja Enter/Tab medan listan visas — annars läcker tangenten
        // till textarean (radbrytning/fokushopp) när kliniken saknar fraser.
        e.preventDefault();
        if (filteredPhrases.length > 0) {
          selectPhrase(filteredPhrases[selectedIndex]);
        } else {
          closeDropdown();
        }
        return true;

      case 'Escape':
        e.preventDefault();
        closeDropdown();
        return true;

      default:
        break;
    }

    return false;
    // selectPhrase/closeDropdown avsiktligt utanför deps: de är const som
    // deklareras nedan (TDZ i deps-arrayen) och recreas när triggern öppnar dropdownen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown, filteredPhrases, selectedIndex]);

  // Select a phrase and insert it
  const selectPhrase = useCallback((phrase) => {
    if (!textareaRef?.current || triggerPosition === null) return;

    const textarea = textareaRef.current;
    const value = textarea.value;
    const cursorPos = textarea.selectionStart;

    // Replace ;searchQuery with phrase text
    const before = value.substring(0, triggerPosition);
    const after = value.substring(cursorPos);
    const newValue = before + phrase.phrase_text + after;

    // Update textarea value
    if (onTextChange) {
      onTextChange(newValue);
    }

    // Set cursor position after inserted text
    const newCursorPos = triggerPosition + phrase.phrase_text.length;
    setTimeout(() => {
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);

    closeDropdown();
  }, [textareaRef, triggerPosition, onTextChange]);

  // Close dropdown and reset state
  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setSearchQuery('');
    setSelectedIndex(0);
    setTriggerPosition(null);
    isAutocompleteActive.current = false;
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDropdown && textareaRef?.current && !textareaRef.current.contains(e.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, textareaRef, closeDropdown]);

  return {
    phrases,
    loading,
    showDropdown,
    dropdownPosition,
    searchQuery,
    filteredPhrases,
    selectedIndex,
    handleKeyDown,
    handleInput,
    selectPhrase,
    closeDropdown,
    refreshPhrases: loadPhrases
  };
}

export default useQuickPhrases;
